/* Supplier-controlled payment cycles for subscriptions.
 *
 * Each branch picks how it bills subscribers:
 *   - 'per_delivery'  → customer is auto-charged before every delivery
 *                        (UPI Autopay or COD-per-delivery). No upfront charge
 *                        beyond a ₹1 mandate token if Autopay.
 *   - 'weekly'        → customer prepays one week of deliveries; auto-recharge
 *                        every week thereafter.
 *   - 'monthly'       → customer prepays one month of deliveries; auto-recharge
 *                        every month thereafter.
 *
 * Helpers below compute the upfront amount, deliveries-per-cycle, and human
 * label so the customer PWA can render the supplier's terms inside Subscribe
 * modal + Routine review.
 */

const COUNT_CUSTOM_DAYS = (customDays) => {
  if (!customDays) return 0;
  return Object.values(customDays).filter(Boolean).length;
};

/** Deliveries per supplier billing cycle for a given cadence. */
export function deliveriesPerCycle(paymentCycle, cadence, customDays) {
  if (paymentCycle === 'per_delivery') return 1;
  if (paymentCycle === 'weekly') {
    if (cadence === 'Daily') return 7;
    if (cadence === 'Alt-day') return 4;          // ceil(7/2)
    if (cadence === 'Weekly') return 1;
    if (cadence === 'Custom') return COUNT_CUSTOM_DAYS(customDays);
  }
  if (paymentCycle === 'monthly') {
    if (cadence === 'Daily') return 30;
    if (cadence === 'Alt-day') return 15;
    if (cadence === 'Weekly') return 4;
    if (cadence === 'Custom') return COUNT_CUSTOM_DAYS(customDays) * 4;
  }
  return 1;
}

/** Upfront amount the customer must pay to start the subscription. */
export function initialChargePaise(paymentCycle, pricePaise, qty, cadence, customDays) {
  const n = deliveriesPerCycle(paymentCycle, cadence, customDays);
  return pricePaise * qty * n;
}

/** Short human label for the supplier's billing policy. */
export function paymentCycleLabel(paymentCycle) {
  if (paymentCycle === 'per_delivery') return 'Pay per delivery';
  if (paymentCycle === 'weekly') return 'Pay weekly';
  if (paymentCycle === 'monthly') return 'Pay monthly';
  return 'Pay per delivery';
}

/** Longer descriptive copy for the policy. */
export function paymentCycleDescription(paymentCycle) {
  if (paymentCycle === 'per_delivery') return 'Auto-charged before each delivery via UPI Autopay (or cash on delivery).';
  if (paymentCycle === 'weekly') return 'Prepay one week of deliveries now. Auto-recharges weekly after that.';
  if (paymentCycle === 'monthly') return 'Prepay one month of deliveries now. Auto-recharges monthly after that.';
  return '';
}

/** Short cycle suffix for use in CTAs ("· weekly recharge"). */
export function cycleRechargeSuffix(paymentCycle) {
  if (paymentCycle === 'per_delivery') return 'per delivery';
  if (paymentCycle === 'weekly') return 'weekly recharge';
  if (paymentCycle === 'monthly') return 'monthly recharge';
  return '';
}
